// src/utils/axiosInterceptor.js

import axios from "axios";
import { apiEndpoints, baseUrl } from "./ApiConfig";
import {
  clearAuthSessionClientState,
  getRefreshTokenFallback,
  isPublicAuthPath,
  persistRefreshTokenFallback,
} from "./authSession";

const isAuthStatusUrl = (url = "") => String(url).includes("/status/");

const isRefreshUrl = (url = "") =>
  String(url).includes("/token/refresh/") || String(url).includes("/api/token/refresh/");

const axiosInterceptor = axios.create({
  baseURL: `${baseUrl}/`,
  withCredentials: true,
});

/** Refresh a few minutes before the 30-minute access cookie expires */
const PROACTIVE_REFRESH_MS = 25 * 60 * 1000;

let isRefreshing = false;
let failedQueue = [];
/** Updated only when /api/token/refresh/ succeeds — not on every API call */
let lastSuccessfulRefreshAt = Date.now();
let sessionRedirectStarted = false;

axiosInterceptor.interceptors.request.use((config) => {
  if (isAuthStatusUrl(config.url)) {
    config.params = { ...config.params, _: Date.now() };
  }
  return config;
});

/**
 * Call from UI catch blocks — skip toast when session expiry is handled globally.
 */
export function shouldShowApiErrorToast(error) {
  if (!error) return false;
  if (error._sessionExpired) return false;
  if (error._authSilent) return false;
  return true;
}

export { persistRefreshTokenFallback, clearAuthSessionClientState } from "./authSession";

const processQueue = (error = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

const redirectToLogin = async () => {
  if (sessionRedirectStarted) return;
  if (isPublicAuthPath()) return;

  sessionRedirectStarted = true;
  clearAuthSessionClientState();
  if (!window.location.pathname.includes("/login")) {
    window.location.replace("/login");
  }
};

const markSessionExpired = (error) => {
  const err = error || new Error("Session expired");
  err._sessionExpired = true;
  return err;
};

const markAuthSilent = (error) => {
  const err = error || new Error("Not authenticated");
  err._authSilent = true;
  return err;
};

export async function refreshAccessTokenSilently() {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const fallbackRefresh = getRefreshTokenFallback();
    const payload = fallbackRefresh ? { refresh: fallbackRefresh } : {};
    const res = await axiosInterceptor.post(apiEndpoints.refreshTokenApi, payload);
    if (res?.data?.refresh) {
      persistRefreshTokenFallback(res.data.refresh);
    }
    lastSuccessfulRefreshAt = Date.now();
    sessionRedirectStarted = false;
    processQueue();
    return true;
  } catch (refreshError) {
    processQueue(refreshError);
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
}

/** Keep access cookie warm while the tab stays open (avoids burst 401s on dashboard). */
export async function ensureFreshAccessToken() {
  if (Date.now() - lastSuccessfulRefreshAt < PROACTIVE_REFRESH_MS) {
    return;
  }
  try {
    await refreshAccessTokenSilently();
  } catch {
    /* Next API call will refresh or redirect */
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      ensureFreshAccessToken();
    }
  });

  window.setInterval(() => {
    if (document.visibilityState === "visible") {
      ensureFreshAccessToken();
    }
  }, PROACTIVE_REFRESH_MS);
}

async function retryAfterRefresh(originalRequest, error) {
  if (originalRequest._retry) {
    const sessionErr = markSessionExpired(error);
    await redirectToLogin();
    return Promise.reject(sessionErr);
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(() => {
        originalRequest._retry = true;
        return axiosInterceptor(originalRequest);
      })
      .catch((queuedError) => Promise.reject(queuedError));
  }

  originalRequest._retry = true;

  try {
    await refreshAccessTokenSilently();
    return axiosInterceptor(originalRequest);
  } catch (refreshError) {
    if (isAuthStatusUrl(originalRequest.url) && isPublicAuthPath()) {
      return Promise.reject(markAuthSilent(refreshError));
    }
    const sessionErr = markSessionExpired(refreshError);
    await redirectToLogin();
    return Promise.reject(sessionErr);
  }
}

axiosInterceptor.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error?.config;

    if (!error.response) {
      return Promise.reject(error);
    }

    if (!originalRequest || isRefreshUrl(originalRequest.url)) {
      if (isPublicAuthPath()) {
        return Promise.reject(markAuthSilent(error));
      }
      await redirectToLogin();
      return Promise.reject(markSessionExpired(error));
    }

    const status = error.response.status;

    if (status !== 401) {
      return Promise.reject(error);
    }

    return retryAfterRefresh(originalRequest, error);
  }
);

export default axiosInterceptor;
